import React, { useState, useRef, useCallback, useEffect } from "react";
import "./App.css";

const THEME_KEY = "ers-theme";

function getInitialTheme() {
  if (typeof window === "undefined") return "light";
  const saved = localStorage.getItem(THEME_KEY);
  if (saved === "light" || saved === "dark") return saved;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

const App = () => {
  const [theme, setTheme] = useState(getInitialTheme);
  const [isRecording, setIsRecording] = useState(false);

  useEffect(() => {
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((t) => (t === "dark" ? "light" : "dark"));
  };
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);

  const stopScreenRecord = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    mediaRecorderRef.current = null;
    setIsRecording(false);
  }, []);

  const recordScreen = async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
      streamRef.current = stream;

      const mime = MediaRecorder.isTypeSupported("video/webm; codecs=vp9")
        ? "video/webm; codecs=vp9"
        : "video/webm";

      const mediaRecorder = new MediaRecorder(stream, { mimeType: mime });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.addEventListener("dataavailable", (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      });

      mediaRecorder.addEventListener("stop", () => {
        const chunks = chunksRef.current;
        if (!chunks.length) {
          setIsRecording(false);
          return;
        }
        const blob = new Blob(chunks, { type: chunks[0].type });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "video.webm";
        a.click();
        URL.revokeObjectURL(url);
        setIsRecording(false);
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        mediaRecorderRef.current = null;
      });

      stream.getVideoTracks()[0]?.addEventListener("ended", () => {
        if (mediaRecorderRef.current?.state === "recording") {
          mediaRecorderRef.current.stop();
        }
      });

      setIsRecording(true);
      mediaRecorder.start();
    } catch (err) {
      console.error(err);
      setIsRecording(false);
    }
  };

  const scrollTo = (id) => (e) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const isDark = theme === "dark";

  return (
    <div className={`ers-app${isDark ? " ers-dark" : ""}`}>
      <header className="ers-header">
        <nav className="ers-nav" aria-label="Main">
          <a href="/" className="ers-logo">
            <span className="emoji" aria-hidden>
              🎥
            </span>
            Easy Record Screen
          </a>
          <div className="ers-nav-links">
            <a href="#features" onClick={scrollTo("features")}>
              Features
            </a>
            <a href="#how-it-works" onClick={scrollTo("how-it-works")}>
              How it works
            </a>
            <a href="#security" onClick={scrollTo("security")}>
              Privacy
            </a>
            <a href="#support" onClick={scrollTo("support")}>
              FAQ
            </a>
            <a
              href="https://www.linkedin.com/in/iamsharmadeepak/"
              target="_blank"
              rel="noopener noreferrer"
            >
              About
            </a>
          </div>
          <div className="ers-nav-cta">
            <button
              type="button"
              className="ers-theme-toggle"
              onClick={toggleTheme}
              aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
              title={isDark ? "Light mode" : "Dark mode"}
            >
              <span className="material-symbols-outlined" aria-hidden>
                {isDark ? "light_mode" : "dark_mode"}
              </span>
            </button>
            <a
              className="ers-btn-github"
              href="https://github.com/asksharmadeepak"
              target="_blank"
              rel="noopener noreferrer"
            >
              GitHub
            </a>
            {isRecording ? (
              <button type="button" className="ers-btn-record-stop" onClick={stopScreenRecord}>
                Stop recording
              </button>
            ) : (
              <button type="button" className="ers-btn-primary" onClick={recordScreen}>
                Start recording
              </button>
            )}
          </div>
        </nav>
      </header>

      <main className="ers-main">
        {/* Hero */}
        <section className="ers-hero" aria-labelledby="hero-heading">
          <div className="ers-hero-inner">
            <div>
              <h1 id="hero-heading">
                Record your screen instantly,{" "}
                <span className="accent">everywhere.</span>
              </h1>
              <p className="ers-hero-lead">
                Use easyrecordscreen.in from home, office, school, or on the go — any device with a
                modern browser. No signup, no install. When you&apos;re done, your video{" "}
                <strong>downloads straight to your computer</strong> (saved locally in your
                Downloads folder) so you own the file offline.
              </p>
              <div className="ers-hero-actions">
                {isRecording ? (
                  <button type="button" className="ers-btn-record-stop ers-btn-primary-lg" onClick={stopScreenRecord}>
                    Stop recording
                  </button>
                ) : (
                  <button type="button" className="ers-btn-primary ers-btn-primary-lg" onClick={recordScreen}>
                    Start recording
                  </button>
                )}
                <button type="button" className="ers-btn-secondary" onClick={scrollTo("features")}>
                  View features
                </button>
              </div>
            </div>
            <div className="ers-hero-visual">
              <div className="ers-hero-card" aria-hidden>
                <div className="ers-hero-card-inner">
                  <div className="fake-ui-dot" />
                  <span style={{ fontSize: "0.875rem", color: "var(--color-on-variant)", fontWeight: 600 }}>
                    easyrecordscreen.in
                  </span>
                </div>
              </div>
              <div className="ers-hero-badge">
                <div className="fake-ui-dot" style={{ animation: "none", background: "#ba1a1a" }} />
                <span className="live">Recording ready</span>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="ers-section ers-section-muted" id="features">
          <div className="ers-section-inner">
            <div className="ers-section-head">
              <h2>Built for clarity &amp; trust</h2>
              <p>
                Tutorials, demos, and quick captures — instant recording everywhere you browse, with a
                real file saved on your machine.
              </p>
            </div>
            <div className="ers-bento">
              <article className="ers-feature-card">
                <div className="ers-feature-icon indigo">
                  <span className="material-symbols-outlined" aria-hidden>
                    public
                  </span>
                </div>
                <h3>Instant, everywhere</h3>
                <p>
                  Same tool in any country — India, US, UK, or anywhere else. Works on Windows, Mac,
                  Linux, and Chromebooks in Chrome, Edge, Brave, and other Chromium browsers (and
                  Safari where supported). Open the site and start in seconds.
                </p>
              </article>
              <article className="ers-feature-card">
                <div className="ers-feature-icon teal">
                  <span className="material-symbols-outlined" aria-hidden>
                    verified_user
                  </span>
                </div>
                <h3>Privacy first</h3>
                <p>
                  Recording runs in your browser. Nothing is uploaded to our servers during capture —
                  your pixels stay on your device until you choose to save.
                </p>
              </article>
              <article className="ers-feature-card">
                <div className="ers-feature-icon indigo">
                  <span className="material-symbols-outlined" aria-hidden>
                    download
                  </span>
                </div>
                <h3>Download locally</h3>
                <p>
                  When you stop recording, the file downloads automatically to your computer — your
                  usual Downloads folder. Keep it offline, edit it, or upload it only if you want.
                  WebM (VP9 when supported) for crisp quality.
                </p>
              </article>
            </div>
          </div>
        </section>

        {/* Ad 1 */}
        <div className="ers-ad-wrap">
          <div className="ers-ad-slot">
            <span className="ers-ad-placeholder">Advertisement — insert Google AdSense code here</span>
          </div>
        </div>

        {/* How it works */}
        <section className="ers-section" id="how-it-works">
          <div className="ers-section-inner">
            <div className="ers-steps-layout">
              <div>
                <div className="ers-section-head" style={{ textAlign: "left", marginBottom: "2rem" }}>
                  <h2 style={{ textAlign: "left" }}>Capture in three steps</h2>
                  <p style={{ margin: 0, maxWidth: "28rem" }}>
                    Pick what to share, record in the browser, then get a file saved locally on your
                    device — no cloud required for the download.
                  </p>
                </div>
                <div className="ers-steps">
                  <div className="ers-step-row">
                    <div className="ers-step-num">01</div>
                    <div>
                      <h4>Select source</h4>
                      <p>
                        Choose full screen, a window, or a browser tab when your browser prompts you.
                      </p>
                    </div>
                  </div>
                  <div className="ers-step-row">
                    <div className="ers-step-num">02</div>
                    <div>
                      <h4>Record locally</h4>
                      <p>
                        Capture happens in your browser. No account and no cloud upload for the
                        recording itself.
                      </p>
                    </div>
                  </div>
                  <div className="ers-step-row" style={{ marginBottom: 0 }}>
                    <div className="ers-step-num">03</div>
                    <div>
                      <h4>Download to your computer</h4>
                      <p>
                        When you stop, your browser saves the recording locally — typically as{" "}
                        <code style={{ fontSize: "0.9em", opacity: 0.9 }}>video.webm</code> in your
                        Downloads folder (Windows, Mac, or Linux). No watermarks from us; the file is
                        yours to keep offline or share however you like.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="ers-steps-visual">
                <div className="ers-steps-visual-inner" aria-hidden />
              </div>
            </div>
          </div>
        </section>

        {/* Ad 2 */}
        <div className="ers-ad-wrap">
          <div className="ers-ad-slot">
            <span className="ers-ad-placeholder">Advertisement — insert Google AdSense code here</span>
          </div>
        </div>

        {/* Trust / Privacy */}
        <section className="ers-section ers-trust" id="security">
          <div className="ers-section-inner" style={{ textAlign: "center" }}>
            <div className="ers-trust-chip">
              <span className="material-symbols-outlined" style={{ fontSize: "1rem" }} aria-hidden>
                lock
              </span>
              Privacy-first recording
            </div>
            <h2 style={{ margin: "0 0 2rem", fontSize: "clamp(1.75rem, 4vw, 2.25rem)" }}>
              Your screen, your data
            </h2>
            <div className="ers-trust-badges">
              <div className="ers-trust-badge">
                <span className="material-symbols-outlined" aria-hidden>
                  lock
                </span>
                <span className="label">HTTPS site</span>
              </div>
              <div className="ers-trust-badge">
                <span className="material-symbols-outlined" aria-hidden>
                  computer
                </span>
                <span className="label">Local processing</span>
              </div>
              <div className="ers-trust-badge">
                <span className="material-symbols-outlined" aria-hidden>
                  gpp_good
                </span>
                <span className="label">No upload during capture</span>
              </div>
            </div>
            <div className="ers-cta-box">
              <h3>Ready to record?</h3>
              {isRecording ? (
                <button type="button" className="ers-btn-record-stop ers-btn-primary-lg" onClick={stopScreenRecord}>
                  Stop recording
                </button>
              ) : (
                <button type="button" className="ers-btn-primary ers-btn-primary-lg" onClick={recordScreen}>
                  Start now — it&apos;s free
                </button>
              )}
              <p className="fine">
                No account required. Record anywhere, download locally — easyrecordscreen.in.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="ers-section" id="support">
          <div className="ers-section-inner">
            <div className="ers-section-head">
              <h2>Frequently asked questions</h2>
              <p>Quick answers about Easy Record Screen and recording online for free.</p>
            </div>
            <div className="ers-faq-list">
              <article className="ers-faq-item">
                <h4>Is this screen recorder free to use in India?</h4>
                <p>
                  Yes. Easy Record Screen is free to use with no hidden fees. It works from India and
                  anywhere you have a supported browser.
                </p>
              </article>
              <article className="ers-faq-item">
                <h4>How do I record my screen online for free?</h4>
                <p>
                  Click &quot;Start recording&quot;, choose what to share when your browser asks, then click
                  &quot;Stop recording&quot; when you&apos;re done. Your file downloads automatically to your
                  computer (local save — check your Downloads folder).
                </p>
              </article>
              <article className="ers-faq-item">
                <h4>Where is my recording saved? Is it on my PC?</h4>
                <p>
                  Yes. After you stop, the browser downloads the video to your machine — usually your
                  Downloads folder as a WebM file. It&apos;s stored locally on your disk; we don&apos;t keep a
                  copy on our servers from that download step.
                </p>
              </article>
              <article className="ers-faq-item">
                <h4>Is my data secure? Does it leave my device?</h4>
                <p>
                  Recording is processed in your browser. We don&apos;t receive your screen video during
                  capture — it isn&apos;t uploaded to our servers as part of recording.
                </p>
              </article>
            </div>
          </div>
        </section>

        {/* Ad 3 */}
        <div className="ers-ad-wrap">
          <div className="ers-ad-slot">
            <span className="ers-ad-placeholder">Advertisement — insert Google AdSense code here</span>
          </div>
        </div>
      </main>

      <footer className="ers-footer">
        <div className="ers-footer-inner">
          <div>
            <div className="ers-footer-brand">Easy Record Screen</div>
            <p className="ers-footer-copy">
              © {new Date().getFullYear()} easyrecordscreen.in — free online screen recorder.
            </p>
          </div>
          <div className="ers-footer-links">
            <a href="mailto:support@easyrecordscreen.in" className="ers-footer-mail">
              support@easyrecordscreen.in
            </a>
            <a href="https://easyrecordscreen.in/">easyrecordscreen.in</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;
